import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { AttachmentsActions } from '@core/attachments-data';
import type { Attachment } from '@core/attachments-types';
import { filterBoolean } from '@core/common-legacy';
import { AttachmentsListComponent } from '@core/components-attachments-list-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  EmptyStateComponent,
  IconComponent,
} from '@core/user-interface-legacy';
import {
  getHumanReadableFileSize,
  MaxFileSize,
  validateMaxFileSize,
  generateUuid,
} from '@core/utils-legacy';
import {
  AttachmentUploadService,
  UploadingAttachmentsItemComponent,
} from '@recruitee/attachments-legacy';
import type { FileWithData } from '@recruitee/attachments-legacy';

import { RequisitionsGeneralFacade } from '../../../facades/single-requisition/requisitions-general.facade';
import { RequisitionsDialogsService } from '../../../services/requisitions-dialogs.service';

@Component({
  selector: 'rt-requisitions-files-container',
  templateUrl: './requisitions-files-container.component.html',
  styleUrls: ['./requisitions-files-container.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    CommonModule,
    EmptyStateComponent,
    IconComponent,
    UploadingAttachmentsItemComponent,
    TranslatePipe,
    AttachmentsListComponent,
  ],
})
export class RequisitionsFilesContainerComponent {
  private readonly requisitionsDialogsService = inject(RequisitionsDialogsService);
  private readonly i18n = inject(I18nService);
  private readonly attachmentUploadService = inject(AttachmentUploadService);
  private readonly requisitionsFacade = inject(RequisitionsGeneralFacade);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly attachments = input<Attachment[]>(undefined!);
  public readonly requisitionId = input<number>(undefined!);

  public canManageFiles: boolean;
  public filesQueue: FileWithData[];
  public uploadError = false;
  public uploading = false;
  public errorText = '';
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.canManageFiles = this.requisitionsFacade.canManageFiles;
    this.subscribeToUploadAttachmentsActions();

    if (this.attachmentUploadService.filesQueue) {
      this.setFilesQueue();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public uploadAttachment(files: FileList | null, fileInput: HTMLInputElement): void {
    if (files && files.length > 0) {
      this.uploadError = false;
      this.uploading = true;
      this.cdRef.detectChanges();

      const file = files[0];

      if (!validateMaxFileSize(file, MaxFileSize.File50MB)) {
        this.uploading = false;
        this.uploadError = true;
        this.errorText = this.i18n.translate('app.common.errors.file_size_limit_exceeded', {
          limit: getHumanReadableFileSize(MaxFileSize.File50MB),
          fileSize: getHumanReadableFileSize(file.size),
        });
        this.cdRef.detectChanges();

        return;
      }

      this.store.dispatch(
        AttachmentsActions.uploadAttachmentPessimistic({
          payload: {
            file: file,
            data: {
              key: 'requisitionId',
              value: this.requisitionId(),
            },
            correlationId: generateUuid(),
          },
        }),
      );
    }
    fileInput.value = '';
  }

  public deleteAttachment(attachment: Attachment): void {
    this.requisitionsDialogsService
      .confirmDeleteAttachment$()
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.store.dispatch(AttachmentsActions.deleteAttachment({ payload: attachment.id }));
      });
  }

  public trackByFileData(index: number): number {
    return index;
  }

  private subscribeToUploadAttachmentsActions(): void {
    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticSuccess),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        this.uploading = false;
        this.cdRef.detectChanges();
      });

    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(error => {
        this.uploading = false;
        this.uploadError = true;
        this.errorText =
          error.payload.error?.error[0] ||
          this.i18n.translate('app.offer_attachments.upload_failed');
        this.cdRef.detectChanges();
      });
  }

  private setFilesQueue(): void {
    this.attachmentUploadService.filesInUpload$
      .asObservable()
      .pipe(
        map(data => data.filter(fileData => fileData.targetId === this.requisitionId())),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(data => {
        this.filesQueue = data;
        this.cdRef.detectChanges();
      });
  }
}
